import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { Check, Globe, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useProjectsContext } from 'src/modules/projects/context';

import { isSolanaChainId } from '@/config/solana';
import { isStellarChainId } from '@/config/stellar';
import { Button, Spinner, Tooltip } from '@/design-system/components/ui';
import useConfirm from '@/hooks/useConfirm';
import useGetChainInfo from '@/hooks/useGetChainInfo';

export const WrongNetwork = () => {
  const { project } = useProjectsContext();
  const [switchNetworkDialog, confirm] = useConfirm();
  const { primaryWallet, network: connectedNetwork } = useDynamicContext();
  const { getChainName } = useGetChainInfo();
  const [isPending, setIsPending] = useState(false);
  const [attemptedSwitch, setAttemptedSwitch] = useState(false);

  const shouldSwitchTo = useMemo(() => project?.contractChainId, [project?.contractChainId]);

  // For Solana projects, skip EVM network check (Solana wallet is checked separately)
  const isSolanaProject = useMemo(() => shouldSwitchTo != null && isSolanaChainId(shouldSwitchTo), [shouldSwitchTo]);

  // Dynamic reports Stellar networks with its own chain ids, never our synthetic one
  const isStellarProject = useMemo(() => shouldSwitchTo != null && isStellarChainId(shouldSwitchTo), [shouldSwitchTo]);

  const isCorrectNetwork = useMemo(
    () => isSolanaProject || isStellarProject || String(connectedNetwork) === String(shouldSwitchTo),
    [connectedNetwork, shouldSwitchTo, isSolanaProject, isStellarProject],
  );
  const networkName = getChainName(shouldSwitchTo);

  useEffect(() => {
    if (isCorrectNetwork && attemptedSwitch) {
      setAttemptedSwitch(false);
    }
  }, [isCorrectNetwork, attemptedSwitch]);

  const handleSwitchNetwork = async () => {
    if (isCorrectNetwork) return;

    const displayName = networkName || `Network ${shouldSwitchTo}`;
    const confirmed = await confirm({
      title: 'Switch Network',
      message: `Your program is hosted on ${displayName}. Please switch to the correct network to view the funds in your wallet and make onchain transactions.`,
      confirmButtonText: `Switch to ${displayName}`,
    });

    if (confirmed && primaryWallet?.connector?.supportsNetworkSwitching() && shouldSwitchTo) {
      setIsPending(true);
      setAttemptedSwitch(true);
      try {
        const chainId = typeof shouldSwitchTo === 'string' ? Number(shouldSwitchTo) : shouldSwitchTo;
        await primaryWallet.switchNetwork(chainId);
      } catch (_error) {
        toast.error('Error switching network');
        setAttemptedSwitch(false);
      } finally {
        setIsPending(false);
      }
    }
  };

  return (
    <>
      <Tooltip
        content={
          isCorrectNetwork
            ? "You're connected to the correct network"
            : "You're not connected to the network where the program is hosted. Funds from another network will not appear until you switch to the correct one."
        }
      >
        <Button variant="ghost" size="icon" onClick={handleSwitchNetwork} disabled={isPending}>
          {isPending ? (
            <Spinner />
          ) : (
            <div className="tw:relative tw:inline-flex">
              <Globe />
              <span
                className={`tw:absolute tw:-top-1 tw:-right-1 tw:rounded-full tw:p-[2px] ${isCorrectNetwork ? 'tw:bg-green-500' : 'tw:bg-red-500'}`}
              >
                {isCorrectNetwork ? <Check size={10} color="white" /> : <X size={10} color="white" />}
              </span>
            </div>
          )}
        </Button>
      </Tooltip>
      {switchNetworkDialog}
    </>
  );
};
